import React, { useState, useEffect } from "react";
// Bootstrap is loaded globally, don't bundle it
import "./kiosk-check-in.css";
import moment from "moment";

const CheckInResult = {
  NoResultsFound: "NoResultsFound",
  MultipleResultsFound: "MultipleResultsFound",
  CheckedIn: "CheckedIn",
  AlreadyCheckedIn: "AlreadyCheckedIn"
} as const;

type CheckInResultCode = (typeof CheckInResult)[keyof typeof CheckInResult];

interface AgendaItemResult {
  userAgendaItemId: number;
  displayOrder: number;
  agendaItemTitle: string;
  disposition: number;
  meetingAgendaItemId?: number;
}

interface RsvpResult {
  id: number;
  name: string;
  attendInPerson: boolean;
  timeStampUtc: string;
}

interface ExistingRsvp {
  rsvp: RsvpResult;
  agendaItems: AgendaItemResult[];
}

interface Confirmation {
  agendaItems: AgendaItemResult[];
}

const getDispositionIcon = (disposition: number): React.ReactElement => {
  switch (disposition) {
    case 0:
      return <span style={{ color: "#808080" }}>(Neutral)</span>;
    case 1:
      return <span style={{ color: "#008000" }}>(Support)</span>;
    case 2:
      return <span style={{ color: "#FF0000" }}>(Oppose)</span>;
    default:
      return <></>;
  }
};

interface NoResultsModalProps {
  onClose: () => void;
  offlineEventName: string;
  onRegister: () => void;
}

const NoResultsModal: React.FC<NoResultsModalProps> = ({
  onClose,
  offlineEventName,
  onRegister
}) => {
  return (
    <div className="modal show d-block" tabIndex={-1}>
      <div className="modal-dialog">
        <div className="modal-content" style={{ marginTop: "20vh" }}>
          <div
            className="modal-header flex-column"
            style={{ textAlign: "left" }}
          >
            <h3 className="mb-0">Check in for: {offlineEventName}</h3>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <div
              className="alert alert-danger"
              role="alert"
              style={{ textAlign: "left" }}
            >
              <h4 className="alert-heading">Registration Not Found</h4>
              <p>
                We couldn't locate your registration. Please double-check your
                information and try again. If you continue to experience issues,
                consider asking for help from staff, or registering again.
              </p>
            </div>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Back to Check-In
            </button>
            <button
              className="btn btn-primary pi-btn-primary"
              onClick={onRegister}
            >
              Register
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface MultipleResultsModalProps {
  multipleResults: ExistingRsvp[];
  onSelectRsvp: (rsvpId: number) => void;
  offlineEventName: string;
  onClose: () => void;
  onRegister: () => void;
}

const MultipleResultsModal: React.FC<MultipleResultsModalProps> = ({
  multipleResults,
  onSelectRsvp,
  offlineEventName,
  onClose,
  onRegister
}) => {
  return (
    <div className="modal show d-block" tabIndex={-1}>
      <div className="modal-dialog modal-lg" style={{ marginTop: "20vh" }}>
        <div className="modal-content">
          <div
            className="modal-header flex-column"
            style={{ textAlign: "left" }}
          >
            <h3 style={{ textAlign: "left" }}>
              Check in for: {offlineEventName}
            </h3>
            <h1 className="modal-title" style={{ textAlign: "left" }}>
              Multiple Registrations Found
            </h1>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <p>Please select your registration:</p>
            {multipleResults.map((option) => (
              <div key={option.rsvp.id} className="card mb-3">
                <div className="card-body" style={{ textAlign: "start" }}>
                  <h5 className="card-title">
                    {option.rsvp.name} (
                    {option.rsvp.attendInPerson ? "In Person" : "By Phone"})
                  </h5>
                  <h6>Agenda Items</h6>
                  {option.agendaItems?.length > 0 && (
                    <ul>
                      {option.agendaItems.map((item) => (
                        <li key={item.userAgendaItemId}>
                          {item.displayOrder + 1}. {item.agendaItemTitle}{" "}
                          {getDispositionIcon(item.disposition)}
                        </li>
                      ))}
                    </ul>
                  )}
                  <p>
                    Registered{" "}
                    {moment
                      .utc(option.rsvp.timeStampUtc)
                      .local()
                      .format("h:mm A - MMMM DD")}
                  </p>
                  <button
                    className="btn btn-primary pi-btn-primary"
                    onClick={() => onSelectRsvp(option.rsvp.id)}
                  >
                    Check In
                  </button>
                </div>
              </div>
            ))}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onRegister}>
              Register
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface SuccessModalProps {
  onClose: () => void;
  offlineEventName: string;
  confirmation: Confirmation | null;
  alreadyCheckedIn: boolean;
}

const SuccessModal: React.FC<SuccessModalProps> = ({
  onClose,
  offlineEventName,
  confirmation,
  alreadyCheckedIn
}) => {
  return (
    <div className="modal show d-block" tabIndex={-1}>
      <div className="modal-dialog" style={{ marginTop: "20vh" }}>
        <div className="modal-content">
          <div
            className="modal-header flex-column"
            style={{ textAlign: "left" }}
          >
            <h3 style={{ textAlign: "left" }}>
              Check in for: {offlineEventName}
            </h3>
            <h1 className="modal-title" style={{ textAlign: "left" }}>
              Success!
            </h1>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            {confirmation?.agendaItems?.length &&
              confirmation.agendaItems.length > 0 && (
                <>
                  <p>
                    {alreadyCheckedIn
                      ? "You have already checked in to this meeting. You are registered to speak on the following agenda items:"
                      : "You have successfully checked in to speak on the following agenda items:"}
                  </p>
                  <ul>
                    {confirmation.agendaItems.map((i) => (
                      <li
                        key={i.userAgendaItemId}
                        style={{ textAlign: "left" }}
                      >
                        Agenda Item {i.displayOrder + 1}: {i.agendaItemTitle}{" "}
                        {getDispositionIcon(i.disposition)}
                      </li>
                    ))}
                  </ul>
                </>
              )}
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Complete Check-In
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface ErrorModalProps {
  onClose: () => void;
  errorMessage: string;
  offlineEventName: string;
}

const ErrorModal: React.FC<ErrorModalProps> = ({
  onClose,
  errorMessage,
  offlineEventName
}) => {
  return (
    <div className="modal show d-block" tabIndex={-1}>
      <div className="modal-dialog" style={{ marginTop: "20vh" }}>
        <div className="modal-content">
          <div
            className="modal-header flex-column"
            style={{ textAlign: "left" }}
          >
            <h3 style={{ textAlign: "left" }}>
              Check in for: {offlineEventName}
            </h3>
            <h1 className="modal-title" style={{ textAlign: "left" }}>
              Error
            </h1>
            <button
              type="button"
              className="btn-close"
              onClick={onClose}
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-body">
            <p>{errorMessage || "An error occurred."}</p>
          </div>
          <div className="modal-footer">
            <button className="btn btn-secondary" onClick={onClose}>
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

interface KioskCheckInComponentProps {
  offlineEventId: number;
  offlineEventName: string;
  submitButtonText?: string;
  onRegisterSelected: () => void;
  onRegisterCompleted: () => void;
  onRegistrationCanceled: () => void;
}

interface MeetingAgendaItem {
  id: number;
  displayOrder: number;
  title: string;
}

interface UserMeetingAgendaItem {
  meetingAgendaItemId: number;
  disposition: number;
  userAgendaItemId?: number;
}

export const KioskCheckInComponent: React.FC<KioskCheckInComponentProps> = ({
  offlineEventId,
  offlineEventName,
  submitButtonText,
  onRegisterSelected,
  onRegisterCompleted,
  onRegistrationCanceled
}) => {
  const [name, setName] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<string | null>(null);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const [multipleResults, setMultipleResults] = useState<ExistingRsvp[]>([]);
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const handleOnRegister = async (e: Event) => {
      const detail = (
        e as CustomEvent<{ offlineEventId: number; userId: number }>
      ).detail;
      const speakerResponsePromise = fetch(
        `/Meeting/${detail.offlineEventId}/GetMeetingSpeaker/${detail.userId}`,
        {
          method: "GET"
        }
      );
      const agendaResponsePromise = fetch(
        `/MeetingAgenda/ListAgendas/${detail.offlineEventId}`,
        {
          method: "GET"
        }
      );

      const [speakerResponse, agendaResponse] = await Promise.all([
        speakerResponsePromise,
        agendaResponsePromise
      ]);

      if (!speakerResponse.ok || !agendaResponse.ok) {
        return;
      }
      const speakerResponseBody = await speakerResponse.json();
      const agendaResponseBody = await agendaResponse.json();
      const agendaItems: MeetingAgendaItem[] =
        agendaResponseBody.meetingAgendaItems;

      const conf: Confirmation = {
        agendaItems:
          (
            speakerResponseBody.userMeetingAgendaItems as
              | UserMeetingAgendaItem[]
              | undefined
          )?.map((u) => {
            const agendaItem = agendaItems.find(
              (i) => i.id === u.meetingAgendaItemId
            );

            return {
              userAgendaItemId: u.userAgendaItemId ?? 0,
              displayOrder: agendaItem?.displayOrder ?? 0,
              agendaItemTitle: agendaItem?.title ?? "",
              disposition: u.disposition
            };
          }) ?? []
      };

      onRegisterCompleted();

      setConfirmation(conf);
      setModalType(CheckInResult.CheckedIn);
      setShowModal(true);
    };

    window.addEventListener("kiosk:registrationComplete", handleOnRegister);
    return () => {
      window.removeEventListener(
        "kiosk:registrationComplete",
        handleOnRegister
      );
    };
  }, [onRegisterCompleted]);

  useEffect(() => {
    const handleOnCancel = () => {
      onRegistrationCanceled();
    };

    window.addEventListener("kiosk:registrationCanceled", handleOnCancel);
    return () => {
      window.removeEventListener("kiosk:registrationCanceled", handleOnCancel);
    };
  }, [onRegistrationCanceled]);

  const handleCheckIn = async (rsvpId: number | null = null) => {
    if (!name || name.trim() === "") {
      setErrorMessage("Please enter your name to check in.");
      setModalType("Error");
      setShowModal(true);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch("/Project/CheckInKioskUser", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          offlineEventId,
          name: name.trim(),
          rsvpId
        })
      });

      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }

      const data = await response.json();
      const resultCode: string = data.checkInResult?.code;

      if (resultCode === CheckInResult.NoResultsFound) {
        setModalType(CheckInResult.NoResultsFound);
        setShowModal(true);
      } else if (resultCode === CheckInResult.MultipleResultsFound) {
        setMultipleResults(data.existingRsvps || []);
        setModalType(CheckInResult.MultipleResultsFound);
        setShowModal(true);
      } else if (resultCode === CheckInResult.CheckedIn) {
        setModalType(CheckInResult.CheckedIn);
        setConfirmation(data.confirmation);
        setShowModal(true);
      } else if (resultCode === CheckInResult.AlreadyCheckedIn) {
        setModalType(CheckInResult.AlreadyCheckedIn);
        setConfirmation(data.confirmation);
        setShowModal(true);
      } else {
        setErrorMessage("An unexpected result was returned from the server.");
        setModalType("Error");
        setShowModal(true);
      }
    } catch (err: unknown) {
      console.error(err);
      setErrorMessage(
        err instanceof Error ? err.message : "An unknown error occurred."
      );
      setModalType("Error");
      setShowModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectRsvp = (rsvpId: number) => {
    handleCheckIn(rsvpId);
  };

  const handleRegister = () => {
    handleCloseModal(true);
    onRegisterSelected();
  };

  const handleCloseModal = (clearInput = false) => {
    setShowModal(false);
    setModalType(null);
    setMultipleResults([]);

    if (clearInput) {
      setName("");
    }
  };

  const renderSpinner = () => {
    if (
      typeof window !== "undefined" &&
      (window as unknown as { TOOLS?: { spinBlock?: (msg: string) => string } })
        .TOOLS?.spinBlock
    ) {
      return (
        <div
          dangerouslySetInnerHTML={{
            __html: (
              window as unknown as {
                TOOLS: { spinBlock: (msg: string) => string };
              }
            ).TOOLS.spinBlock("Looking up registration...")
          }}
          style={{ minHeight: "200px" }}
        />
      );
    }
    return (
      <div
        className="text-center p-4"
        style={{ minHeight: "200px", paddingTop: "80px" }}
      >
        <div
          className="spinner-border text-primary"
          role="status"
          style={{ width: "3rem", height: "3rem" }}
        >
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-3">Looking up registration...</p>
      </div>
    );
  };

  return (
    <div
      className="text-center"
      style={{
        position: "relative",
        minHeight: isLoading ? "200px" : "auto",
        marginBottom: "2rem"
      }}
    >
      {isLoading && (
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 1000,
            backgroundColor: "rgba(255, 255, 255, 0.95)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          {renderSpinner()}
        </div>
      )}
      <div
        className="text-left form-centered"
        style={{
          opacity: isLoading ? 0.5 : 1,
          pointerEvents: isLoading ? "none" : "auto"
        }}
      >
        <div className="mt-4">
          <div className="mb-3 row">
            <label htmlFor="nameInput" className="col-sm-2 col-form-label">
              Name
            </label>
            <div className="col-sm-10">
              <input
                id="nameInput"
                type="text"
                className="form-control"
                placeholder="Enter your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
        <button
          className="btn btn-primary pi-btn-primary btn-lg me-3"
          onClick={() => handleCheckIn()}
          disabled={isLoading}
        >
          {submitButtonText || "Sign In"}
        </button>
        <button
          className="btn btn-secondary btn-lg"
          onClick={() => handleRegister()}
          disabled={isLoading}
        >
          Register
        </button>
      </div>

      {!isLoading && showModal && modalType === "NoResultsFound" && (
        <NoResultsModal
          onRegister={handleRegister}
          onClose={handleCloseModal}
          offlineEventName={offlineEventName}
        />
      )}

      {!isLoading && showModal && modalType === "MultipleResultsFound" && (
        <MultipleResultsModal
          multipleResults={multipleResults}
          onSelectRsvp={handleSelectRsvp}
          offlineEventName={offlineEventName}
          onRegister={handleRegister}
          onClose={handleCloseModal}
        />
      )}

      {!isLoading &&
        showModal &&
        (modalType === "CheckedIn" || modalType === "AlreadyCheckedIn") && (
          <SuccessModal
            onClose={() => handleCloseModal(true)}
            offlineEventName={offlineEventName}
            confirmation={confirmation}
            alreadyCheckedIn={modalType === "AlreadyCheckedIn"}
          />
        )}

      {!isLoading && showModal && modalType === "Error" && (
        <ErrorModal
          onClose={handleCloseModal}
          offlineEventName={offlineEventName}
          errorMessage={errorMessage}
        />
      )}
    </div>
  );
};
